import { useState } from "react";
import { useNavigate, useLocation, Navigate } from "react-router-dom";
import { useAdminAuth } from "@/context/AdminAuth";
import { formatApiError } from "@/lib/api";
import "./admin/admin.css";

export default function AdminLogin() {
  const { admin, login } = useAdminAuth();
  const nav = useNavigate();
  const loc = useLocation();
  const from = loc.state?.from || "/admin";

  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState("");

  if (admin) return <Navigate to={from} replace />;

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setSubmitting(true);
    try {
      await login(email.trim(), password);
      nav(from, { replace: true });
    } catch (err) {
      const detail = err?.response?.data?.detail;
      const msg = formatApiError(detail) || err?.message || "Invalid email or password.";
      setError(msg);
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div data-testid="admin-login-page" className="adm-login min-h-screen flex text-[#f5f1e8]">
      {/* Venstre: kinematisk brand-panel */}
      <div className="adm-login-pane hidden lg:flex flex-col justify-between w-[56%] p-12 xl:p-16 border-r border-white/[0.07]">
        <div className="adm-grain" />
        <p className="adm-login-rise text-[12px] font-semibold uppercase tracking-[0.2em]">
          Norwegian AI Summit <span className="text-[#F1E75A]">2027</span>
        </p>
        <div className="adm-login-rise-2">
          <p
            className="uppercase font-medium leading-[0.98] tracking-[-0.03em] text-[#f5f1e8]"
            style={{ fontFamily: '"Switzer", "Instrument Sans", sans-serif', fontSize: "clamp(56px, 6.4vw, 108px)" }}
          >
            Event
            <br />
            <span className="text-[#F1E75A]">OS</span>
          </p>
          <p className="mt-6 max-w-[420px] text-[15px] leading-relaxed text-[#8b9199]">
            Ett operativsystem for hele summiten — speakers, program, partnere, experience, saker og budsjett.
          </p>
        </div>
        <div className="adm-login-rise-3 flex items-center gap-10 border-t border-white/[0.08] pt-5">
          {[
            ["Edition", "01 / 2027"],
            ["Venue", "Grieghallen, Bergen"],
            ["Status", "Planning"],
          ].map(([k, v]) => (
            <div key={k}>
              <p className="text-[9px] uppercase tracking-[0.26em] text-[#565c66]">{k}</p>
              <p className="mt-1 text-[12.5px] text-[#b7bdc5] tabular-nums">{v}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Høyre: innlogging */}
      <div className="flex-1 flex items-center justify-center px-6 py-12 relative">
        <div className="w-full max-w-[360px]">
          <div className="lg:hidden text-center mb-10">
            <p className="text-[12.5px] font-semibold uppercase tracking-[0.18em]">
              Norwegian AI Summit <span className="text-[#F1E75A]">2027</span>
            </p>
          </div>

          <p className="adm-login-rise text-[10px] uppercase tracking-[0.3em] text-[#565c66] mb-8">
            Admin access
          </p>

          <form onSubmit={onSubmit} className="adm-login-rise-2 flex flex-col gap-5" data-testid="admin-login-form">
            <label className="flex flex-col gap-2 text-[10px] font-semibold uppercase tracking-[0.16em] text-[#8b9199]">
              Email
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                className="adm-login-input"
                autoComplete="email"
                required
                data-testid="admin-login-email"
              />
            </label>
            <label className="flex flex-col gap-2 text-[10px] font-semibold uppercase tracking-[0.16em] text-[#8b9199]">
              Password
              <input
                type="password"
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="adm-login-input"
                autoComplete="current-password"
                required
                data-testid="admin-login-password"
              />
            </label>

            {error && (
              <p className="text-[13px] text-[#ff6b5a]" data-testid="admin-login-error">
                {error}
              </p>
            )}

            <button
              type="submit"
              disabled={submitting}
              className="adm-login-btn mt-1"
              data-testid="admin-login-submit"
            >
              {submitting ? "Signing in…" : "Sign in"}
            </button>
          </form>

          <div className="adm-login-rise-3 flex items-center justify-between mt-8 border-t border-white/[0.07] pt-4">
            <p className="text-[10.5px] text-[#565c66]">Session cookie · valid 12 hours</p>
            <a href="/" className="text-[10.5px] text-[#8b9199] hover:text-[#f5f1e8] transition-colors">
              norwegianaisummit.no →
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}
